import { Box, Stack } from '@mui/material';
import { GridRowOrderChangeParams, useGridApiRef } from '@mui/x-data-grid-pro';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ZodError } from 'zod';

import { AstroSmartLinkDomain, AstroSmartLinkVariant } from '@/generated/gql/graphql';
import {
  AddRowButton,
  DataGridCustom,
  DataGridWidgetHeader,
  useDataGridStatsTranslation,
} from '~/components/DataGridCustom';
import { useSmartLinks } from '~/components/SmartLinksNavList/hooks/useSmartLinks';

import { useSmartLinkDataGridActions } from './SmartLinkGridForm.actions';
import { useSmartLinkDataGridConfig } from './SmartLinkGridForm.config';
import { smartLinkGridFormSchema } from './SmartLinkGridForm.schema';
import { SmartLinkRow } from './SmatLinkGridForm.types';

export interface SmartLinkGridFormProps {
  domain: AstroSmartLinkDomain;
}

export const SmartLinkGridForm = ({ domain }: SmartLinkGridFormProps) => {
  const apiRef = useGridApiRef();
  const { t: tLinks } = useTranslation(undefined, { keyPrefix: 'SmartLinks' });
  const { t } = useTranslation(undefined, { keyPrefix: 'SmartLinksGrid' });
  const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useCustomSnackbar();
  const { smartLinks, deleteSmartLink, upsertSmartLink } = useSmartLinks({ domain });
  const [rows, setRows] = useState<SmartLinkRow[]>([]);

  useEffect(() => {
    if (smartLinks) {
      setRows(
        smartLinks.map((i) => ({
          description: i.description || '',
          domain: i.domain,
          hidden: i.hidden,
          external: i.external,
          id: i.id,
          title: i.title,
          url: i.url,
          isNew: false,
        })),
      );
    }
  }, [smartLinks]);

  const scrollToRef = useRef<string | null>(null);

  const { rowFactory, config } = useSmartLinkDataGridConfig({ domain });

  const { columns, rowModesModel, onRowModesModelChange, onRowAdd } = useSmartLinkDataGridActions({
    apiRef,
    columns: config.columns,
    onAdd: () => {
      const newRow = rowFactory();
      scrollToRef.current = newRow.id;
      setRows((prevRows) => [...prevRows, newRow]);
      return newRow;
    },
    onDelete: async (id) => {
      deleteSmartLink(id);
      const { errors = [] } = {};
      if (!errors || errors.length === 0) {
        setRows((prevRows) => prevRows.filter((row) => row.id !== id));
      }
    },
    onCancel: (id) => {
      const row = apiRef.current.getRow(id);
      if (row?.isNew) {
        setRows((prevRows) => prevRows.filter((row) => row.id !== id));
      }
    },
  });

  const title = useDataGridStatsTranslation({ rowCount: rows.length || 0 });

  const handleRowUpdate = async ({ isNew, ...row }: SmartLinkRow) => {
    const updatedRow = { ...row, variant: AstroSmartLinkVariant.OTHER, sequence: 0 };
    const { success, data: validated, error } = smartLinkGridFormSchema.safeParse(updatedRow);
    if (!success) {
      throw error;
    }

    const payload = { ...validated, domain };
    await upsertSmartLink(payload);

    enqueueSuccessSnackbar(tLinks('actions.saveSuccess'));
    return row;
  };

  const handleRowChange = async ({ oldIndex, targetIndex }: GridRowOrderChangeParams) => {
    const movedItem = rows[oldIndex];
    const remainingItems = rows.filter((_, index) => index !== oldIndex);

    const reorderedItems = [
      ...remainingItems.slice(0, targetIndex),
      movedItem,
      ...remainingItems.slice(targetIndex),
    ] as SmartLinkRow[];

    await upsertSmartLink(
      reorderedItems.map(({ isNew, ...item }, index) => ({
        id: item.id,
        title: item.title,
        url: item.url,
        variant: AstroSmartLinkVariant.OTHER,
        sequence: index,
      })),
    );
  };

  const handleError = (error: Error) => {
    const errorMessage =
      error instanceof ZodError ? `${error.issues[0].path}: ${error.issues[0].message}` : t(error.message);
    enqueueErrorSnackbar(errorMessage);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', flex: '1 1 0' }}>
      <DataGridWidgetHeader
        title={title}
        left={
          <Stack direction="row">
            <AddRowButton onAddClick={onRowAdd} />
          </Stack>
        }
      />
      <DataGridCustom
        sx={{ height: '100%', flex: '1 1 0' }}
        apiRef={apiRef}
        editMode="row"
        getRowId={(row) => row.id}
        rows={rows}
        rowCount={rows.length}
        columns={columns}
        rowModesModel={rowModesModel}
        onRowModesModelChange={onRowModesModelChange}
        processRowUpdate={handleRowUpdate}
        disableColumnPinning={false}
        disableColumnSelector={false}
        onProcessRowUpdateError={handleError}
        disableColumnMenu
        disableColumnFilter
        disableMultipleColumnsSorting
        slotProps={{ baseButton: { variant: 'text' } }}
        rowReordering
        onRowOrderChange={handleRowChange}
      />
    </Box>
  );
};
